import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import {
  clearPortalToken,
  fetchPortalCustomer,
  getPortalToken,
  setPortalToken,
  type PortalCustomer,
} from '@/lib/portalApi';

interface CustomerInfo {
  id: string;
  name: string;
  email: string;
}

interface PortalContextType {
  customer: CustomerInfo | null;
  companyId: string | null;
  token: string | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  /** Redeem a customer-scoped guest token (from the magic-link URL) and hydrate the session. */
  signIn: (token: string) => Promise<void>;
  signOut: () => void;
}

const PortalContext = createContext<PortalContextType | null>(null);

function toCustomerInfo(dto: PortalCustomer): CustomerInfo {
  return { id: dto.id, name: dto.name, email: dto.email ?? '' };
}

export function PortalProvider({ children }: { children: ReactNode }) {
  const [customer, setCustomer] = useState<CustomerInfo | null>(null);
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [token, setToken] = useState<string | null>(() => getPortalToken());
  const [isLoading, setIsLoading] = useState(true);

  // The portal session IS the customer-scoped guest token (stored in localStorage). On mount,
  // hydrate customer + companyId from /api/portal/me; a rejected token clears the session.
  useEffect(() => {
    let cancelled = false;
    const stored = getPortalToken();
    if (!stored) {
      setIsLoading(false);
      return;
    }

    (async () => {
      try {
        const dto = await fetchPortalCustomer(stored);
        if (cancelled) return;
        setCustomer(toCustomerInfo(dto));
        setCompanyId(dto.companyId);
        setToken(stored);
      } catch {
        if (cancelled) return;
        clearPortalToken();
        setToken(null);
        setCustomer(null);
        setCompanyId(null);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const signIn = useCallback(async (rawToken: string) => {
    const dto = await fetchPortalCustomer(rawToken);
    setPortalToken(rawToken);
    setToken(rawToken);
    setCustomer(toCustomerInfo(dto));
    setCompanyId(dto.companyId);
  }, []);

  const signOut = useCallback(() => {
    clearPortalToken();
    setToken(null);
    setCustomer(null);
    setCompanyId(null);
  }, []);

  return (
    <PortalContext.Provider
      value={{
        customer,
        companyId,
        token,
        isLoading,
        isAuthenticated: !!customer && !!token,
        signIn,
        signOut,
      }}
    >
      {children}
    </PortalContext.Provider>
  );
}

export function usePortal() {
  const context = useContext(PortalContext);
  if (!context) {
    throw new Error('usePortal must be used within PortalProvider');
  }
  return context;
}
